import { useCallback, useMemo, useState } from "react";
import { Select } from "antd";
import { useDebounceCallback } from "@react-hook/debounce";
import { BookOpen, X } from "lucide-react";

import { useGetJurisdictionCodeBooksQuery } from "graphql/queries/get-jurisdiction-code-books.generated";
import { useFindCodeSectionsLazyQuery } from "graphql/queries/find-code-sections.generated";
import { CodeBookType, CodeCitationOrigin } from "constants/field-values";
import { formatSectionNumberForCitation, isAppendixSectionNumber } from "utils/code-section-citation";

// A heading needs a few letters before it means anything, so a shorter query stays a
// number lookup: this pattern is one no heading can contain.
const NO_TEXT_MATCH = "\u0000";
const MIN_TEXT_SEARCH_LENGTH = 3;

// A whole appendix is stored by its letters alone ("D"), not by the word the code prints in front of
// it. The word is removed here, so "Appendix D" is a prefix search on "D": the appendix itself comes
// first (its sort key is "D00000") and its sections follow. "Chapter 5" is a search on "5", which
// finds the sections of chapter 5 but never the whole chapter, because a chapter has no section of
// its own.
const CHAPTER_OR_APPENDIX_PREFIX = /^(?:appendix|chapter)\s+/i;

/** One citation on the rule being edited. Mirrors a plan_review_requirement_code_sections row. */
export interface CodeCitationValue {
	code_section_id: string;
	citation_text: string;
	origin: string;
	/** Filled in as soon as the row is known, so the chip reads as a citation and not as an id. */
	label: string;
}

interface CodeCitationPickerProps {
	jurisdictionId: string;
	value: CodeCitationValue[];
	onChange: (citations: CodeCitationValue[]) => void;
	disabled?: boolean;
}

const bookEditionLabel = (book: { abbreviation: string; edition_year?: number | null }) =>
	`${book.abbreviation}${book.edition_year ? ` ${book.edition_year}` : ""}`;

const bookLabel = (book: { abbreviation: string; edition_year?: number | null; book_type?: string | null }) =>
	`${bookEditionLabel(book)}${book.book_type === CodeBookType.AMENDMENT ? " (local amendments)" : ""}`;

/**
 * Picks the code sections a rule rests on, from the editions this authority adopted.
 *
 * Structured on purpose: the citation used to be a line of free text that read well and
 * resolved to nothing. Choosing the section is what lets the analysis read the code
 * language alongside the rule, and what puts the text in front of anyone reviewing a
 * finding — so a number that is not in the adopted code cannot be picked at all.
 */
const CodeCitationPicker = ({ jurisdictionId, value, onChange, disabled }: CodeCitationPickerProps) => {
	const { data: booksData, loading: loadingBooks } = useGetJurisdictionCodeBooksQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});

	const books = useMemo(
		() => (booksData?.jurisdiction_adopted_codes ?? []).flatMap(row => (row.code_book ? [row.code_book] : [])),
		[booksData]
	);

	const [bookId, setBookId] = useState<string | undefined>();
	const [searchSections, { data: sectionsData, loading: searching }] = useFindCodeSectionsLazyQuery({
		fetchPolicy: "network-only"
	});

	const runSearch = useCallback(
		(typed: string) => {
			const trimmed = typed.trim();
			if (!bookId || !trimmed) return;
			const query = trimmed.replace(CHAPTER_OR_APPENDIX_PREFIX, "");
			searchSections({
				variables: {
					codeBookId: bookId,
					// Matched as a prefix: the manager types "507.5" and picks between that
					// section and the ones nested under it.
					numberSearch: `${query}%`,
					// Matched anywhere in the heading or the rule text, so "hydrant" finds the
					// section without anyone having to know where it sits in the book, even
					// when it's only mentioned in the body.
					textSearch: query.length >= MIN_TEXT_SEARCH_LENGTH ? `%${query}%` : NO_TEXT_MATCH
				}
			});
		},
		[bookId, searchSections]
	);

	// Typing a phrase is several keystrokes, and each one used to be a round trip.
	const handleSearch = useDebounceCallback(runSearch, 300);

	const alreadyPicked = useMemo(() => new Set(value.map(citation => citation.code_section_id)), [value]);

	const options = useMemo(
		() =>
			(sectionsData?.code_sections ?? [])
				.filter(section => !alreadyPicked.has(section.id))
				.map(section => {
					// A whole appendix shows "Appendix D" as its number. Its title is the same text, so it
					// is not shown twice.
					const isAppendix = isAppendixSectionNumber(section.section_number);
					return {
						value: section.id,
						// Two lines rather than one: a result found by its wording is read by its
						// wording, and the number alone would not tell the manager what they found.
						label: (
							<span className="ccp-option">
								<span className="ccp-option-number">{isAppendix ? section.title : section.section_number}</span>
								{section.title && !isAppendix ? <span className="ccp-option-title">{section.title}</span> : null}
							</span>
						),
						sectionNumber: section.section_number
					};
				}),
		[sectionsData, alreadyPicked]
	);

	const handlePick = (sectionId: string) => {
		const book = books.find(candidate => candidate.id === bookId);
		const option = options.find(candidate => candidate.value === sectionId);
		if (!book || !option) return;
		onChange([
			...value,
			{
				code_section_id: sectionId,
				// Written the way the code prints it ("IFC 2021 § 507.5.1", "IFC 2021 Appendix D"),
				// which is what the report shows and what a reviewer expects to read back.
				citation_text: `${bookEditionLabel(book)} ${formatSectionNumberForCitation(option.sectionNumber)}`,
				origin: CodeCitationOrigin.MANUAL,
				label: `${bookEditionLabel(book)} ${formatSectionNumberForCitation(option.sectionNumber)}`
			}
		]);
	};

	if (!loadingBooks && books.length === 0) {
		return (
			<p className="ccp-hint">
				This jurisdiction has no adopted code on file yet, so there is nothing to cite. Ask an administrator to import
				the code edition it enforces.
			</p>
		);
	}

	return (
		<div className="ccp-root">
			<div className="ccp-controls">
				<Select
					className="ccp-book"
					value={bookId}
					placeholder="Code (e.g. IFC 2021)"
					disabled={disabled}
					loading={loadingBooks}
					options={books.map(book => ({ value: book.id, label: bookLabel(book) }))}
					onChange={setBookId}
				/>
				<Select
					className="ccp-section"
					popupClassName="ccp-section-dropdown"
					showSearch
					// The list is already the server's answer to what was typed, so antd must
					// not also filter it against the label it happens to be showing.
					filterOption={false}
					value={null}
					placeholder="Section number, appendix or wording, e.g. 507.5.1, Appendix D or hydrant"
					disabled={disabled || !bookId}
					loading={searching}
					options={options}
					onSearch={handleSearch}
					onChange={handlePick}
					notFoundContent={
						searching ? "Searching…" : "Type a section number, an appendix or a few words from its heading"
					}
				/>
			</div>

			{value.length > 0 && (
				<ul className="ccp-chips">
					{value.map(citation => (
						<li className="ccp-chip" key={citation.code_section_id}>
							<BookOpen size={12} />
							<span>{citation.label || citation.citation_text}</span>
							{citation.origin === CodeCitationOrigin.AI_EXTRACTION && (
								<span className="ccp-chip-origin">from extraction</span>
							)}
							{!disabled && (
								<button
									type="button"
									className="ccp-chip-remove"
									aria-label={`Remove ${citation.label}`}
									onClick={() => onChange(value.filter(other => other.code_section_id !== citation.code_section_id))}
								>
									<X size={12} />
								</button>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default CodeCitationPicker;
